import { TranslateVar } from "@faro-lotv/flat-ui";
import { useMemo } from "react";

type UseSelectedEntitiesDescriptionOptions = {
  /** The number of scans selected */
  scansSelectedCount: number;
  /** The number of clusters selected */
  clustersSelectedCount: number;
  /** The separator to use between scans and clusters (e.g., " • " or " and ") */
  separator?: string;
  /** Whether to append " selected" after each section (default), at the end, or not at all */
  selectedPosition?: "perSection" | "end" | "none";
};

/**
 * A hook to generate a description of the selected entities.
 *
 * @param options - The options for generating the description
 * @returns A React node describing the selected entities
 */
export function useSelectedEntitiesDescription({
  scansSelectedCount,
  clustersSelectedCount,
  separator = " • ",
  selectedPosition = "perSection",
}: UseSelectedEntitiesDescriptionOptions): React.ReactNode {
  return useMemo<React.ReactNode>(() => {
    if (scansSelectedCount + clustersSelectedCount === 0) {
      return null;
    }
    return (
      <>
        {scansSelectedCount > 0 && (
          <>
            <TranslateVar name="selectedScanCount">
              {scansSelectedCount}
            </TranslateVar>{" "}
            {scansSelectedCount === 1 ? "capture" : "captures"}
            {selectedPosition === "perSection" && " selected"}
          </>
        )}

        {scansSelectedCount > 0 && clustersSelectedCount > 0 && separator}

        {clustersSelectedCount > 0 && (
          <>
            <TranslateVar name="selectedClusterCount">
              {clustersSelectedCount}
            </TranslateVar>{" "}
            {clustersSelectedCount === 1 ? "cluster" : "clusters"}
            {selectedPosition === "perSection" && " selected"}
          </>
        )}
        {selectedPosition === "end" && " selected"}
      </>
    );
  }, [scansSelectedCount, clustersSelectedCount, separator, selectedPosition]);
}
